import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
    ChevronDown, ChevronLeft, ChevronRight, ExternalLink, ImagePlus, MessagesSquare, MousePointerClick, ShoppingCart, Sofa, ThumbsDown, ThumbsUp,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import {
    ConversationDetail, ConversationOutcome, ConversationsPage, HandoffFilter, ShopperOverview, useShopperInsightsApi,
} from '../../../common/hooks/use-shopper-insights-api.hook';
import { onRealtime } from '../../../common/services/realtime.service';
import { HANDOFF_LIVE_EVENT, TeamBadge, TeamComposer, TeamStatus, handoffOpen, useHandoffSummary } from './team-panel';
import { FeatureGate } from '../../../components/FeatureGate';
import { UnlockRows } from '../../../components/design-system/plan/UnlockRows';
import { HistoryLimitNotice } from '../../../components/design-system/plan/HistoryLimitNotice';
import { openUnlockDialog } from '../../../components/design-system/plan/plan-limit-store';
import { readPlanError } from '../../../components/design-system/plan/plan-limits';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { PillTabs } from '../../../components/design-system/admin/PillTabs';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { SearchField } from '../../../components/design-system/admin/SearchField';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { formatDateTime, formatNumber } from '../../../components/design-system/admin/format';
import { formatCost, formatSeconds, useInsightsRange } from './assistant-shared';
import { conversationOutcomeOf, reasonOf } from './assistant-reading';
import {
    AssistantHeader, OutcomeBadge, ProductCard, ProductThumbs, ShopperAvatar, useAskArdiAbout, useDeviceLabel, useOutcomeLabel, useReasonLabel,
} from './assistant-ui';

const PAGE_SIZE = 20;

/** The team's filters first (who is waiting for a person), then how conversations ended. */
type Filter = 'all' | HandoffFilter | ConversationOutcome;
const TEAM_FILTERS: HandoffFilter[] = ['waiting', 'live'];
const FILTERS: Filter[] = ['all', ...TEAM_FILTERS, 'unanswered', 'recommended', 'room', 'answered'];
const isTeamFilter = (value: Filter): value is HandoffFilter => (TEAM_FILTERS as string[]).includes(value);
const isFilter = (value: string | null): value is Filter => !!value && (FILTERS as string[]).includes(value);

const EVENT_ICON: Record<string, typeof ShoppingCart> = {
    cart_clicked: ShoppingCart,
    product_link_clicked: MousePointerClick,
    see_in_room_clicked: Sofa,
    visualize_clicked: Sofa,
    open_space_clicked: Sofa,
    chat_opened: MessagesSquare,
};

/** A conversation, as the shopper and the assistant had it: bubbles, the products shown, what the shopper did. */
const Transcript = ({ detail }: { detail: ConversationDetail }) => {
    const { t } = useTranslation();
    const reasonLabel = useReasonLabel();
    const askArdi = useAskArdiAbout();

    let lastQuestion: { text: string; sku: string | null } | null = null;
    const toolCalls = detail.messages.flatMap((m) => (m.role === 'agent' ? m.toolCalls || [] : []));

    return (
        <div className="flex flex-col gap-6 p-6">
            <ol className="flex flex-col gap-4" aria-label={t('SHOPPER_UI.CONVERSATIONS.TRANSCRIPT')}>
                {detail.messages.map((m, index) => {
                    const shopper = m.role === 'visitor';
                    // A person from the shop, answering in the chat (shopper-handoff).
                    const team = m.role === 'team';
                    if (shopper && m.text) lastQuestion = { text: m.text, sku: m.sku || detail.productSku };
                    const asked = lastQuestion;
                    const unanswered = !shopper && !team && !!m.outcome && m.outcome !== 'answered';
                    return (
                        <li key={m.id || index} className={cn('flex flex-col gap-1.5', shopper ? 'items-start' : 'items-end')}>
                            <span className="text-xs text-muted-foreground">
                                {shopper
                                    ? t('SHOPPER_UI.CONVERSATIONS.SHOPPER')
                                    : team
                                        ? (m.author?.name ? t('SHOPPER_TEAM.TEAM_LABEL', { name: m.author.name }) : t('SHOPPER_TEAM.YOUR_TEAM'))
                                        : t('SHOPPER_UI.CONVERSATIONS.ASSISTANT')}
                                <span className="ms-1.5 tabular-nums">{dayjs(m.at).format('HH:mm')}</span>
                            </span>

                            {m.photo?.spaceId ? (
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-dashed px-3 py-2 text-xs text-muted-foreground">
                                    <ImagePlus className="size-3.5" />{t('SHOPPER_UI.CONVERSATIONS.PHOTO_SENT')}
                                </span>
                            ) : m.render?.spaceId && !m.text ? (
                                <span className="inline-flex items-center gap-1.5 rounded-xl border border-dashed px-3 py-2 text-xs text-muted-foreground">
                                    <Sofa className="size-3.5" />{t('SHOPPER_UI.CONVERSATIONS.RENDER_SHOWN')}
                                </span>
                            ) : (
                                <div
                                    className={cn(
                                        'max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                                        shopper ? 'rounded-ss-md bg-muted' : team ? 'rounded-se-md bg-foreground text-background' : 'rounded-se-md border bg-background',
                                    )}
                                >
                                    {m.text || <span className="italic text-muted-foreground">{t('SHOPPER_UI.CONVERSATIONS.EMPTY_REPLY')}</span>}
                                </div>
                            )}

                            {!shopper && (m.products || []).length > 0 && (
                                <div className="flex max-w-[85%] gap-2 overflow-x-auto pb-1">
                                    {m.products.map((p: any, i: number) => <ProductCard key={p.sku || p.uuid || i} product={p} />)}
                                </div>
                            )}

                            {!shopper && (unanswered || m.feedback?.rating) && (
                                <div className="flex flex-wrap items-center justify-end gap-3">
                                    {unanswered && (
                                        <StatusBadge tone="warning" className="text-muted-foreground">
                                            {reasonLabel(reasonOf(m.outcome))}
                                        </StatusBadge>
                                    )}
                                    {unanswered && askArdi && asked && reasonOf(m.outcome) === 'no_match' && (
                                        <Button variant="outline" size="xs" onClick={() => askArdi(asked.text, asked.sku)}>
                                            {t('SHOPPER_UI.ASK_ARDI.LABEL')}
                                        </Button>
                                    )}
                                    {m.feedback?.rating === 'up' && (
                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                            <ThumbsUp className="size-3.5" />{t('SHOPPER_UI.CONVERSATIONS.HELPFUL')}
                                        </span>
                                    )}
                                    {m.feedback?.rating === 'down' && (
                                        <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                            <ThumbsDown className="size-3.5" />{t('SHOPPER_UI.CONVERSATIONS.NOT_HELPFUL')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>

            {detail.events.length > 0 && (
                <section className="flex flex-col gap-2">
                    <h3 className="text-sm font-medium">{t('SHOPPER_UI.CONVERSATIONS.ACTIONS_TITLE')}</h3>
                    <ul className="flex flex-col divide-y rounded-xl border">
                        {detail.events.map((e, i) => {
                            const Icon = EVENT_ICON[e.type] || MousePointerClick;
                            return (
                                <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                                    <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                                    <span className="min-w-0 flex-1 truncate">
                                        {t(`SHOPPING_ASSISTANT.LABELS.${ACTION_LABEL[e.type] || ''}`, { defaultValue: e.type })}
                                        {e.productSku ? <span className="text-muted-foreground"> · {e.productSku}</span> : null}
                                    </span>
                                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{dayjs(e.at).format('HH:mm')}</span>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <Collapsible className="rounded-xl border">
                <CollapsibleTrigger className="group/tech flex w-full items-center justify-between gap-3 px-3 py-2.5 text-start text-sm">
                    {t('SHOPPER_UI.CONVERSATIONS.TECHNICAL')}
                    <ChevronDown className="size-4 text-muted-foreground transition-transform group-data-[state=open]/tech:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent className="border-t px-3 py-3">
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <div>
                            <dt className="text-xs text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.AI_COST')}</dt>
                            <dd className="tabular-nums">{formatCost(detail.costUsd)}</dd>
                        </div>
                        <div>
                            <dt className="text-xs text-muted-foreground">{t('SHOPPER_UI.DASHBOARD.REPLY_TIME')}</dt>
                            <dd className="tabular-nums">{formatSeconds(averageLatency(detail))}</dd>
                        </div>
                        {detail.utm && Object.keys(detail.utm).length > 0 && (
                            <div className="col-span-2">
                                <dt className="text-xs text-muted-foreground">{t('SHOPPER_UI.CONVERSATIONS.CAMPAIGN')}</dt>
                                <dd className="break-words">{Object.entries(detail.utm).map(([k, v]) => `${k}: ${v}`).join(' · ')}</dd>
                            </div>
                        )}
                        {toolCalls.length > 0 && (
                            <div className="col-span-2">
                                <dt className="text-xs text-muted-foreground">{t('SHOPPER_UI.CONVERSATIONS.LOOKUPS')}</dt>
                                <dd className="mt-1 flex flex-wrap gap-1.5">
                                    {toolCalls.map((call: any, i: number) => (
                                        <span key={i} className="rounded-md border px-2 py-0.5 font-mono text-[11px] text-muted-foreground" title={JSON.stringify(call.args)}>
                                            {call.name}{typeof call.resultCount === 'number' ? ` → ${call.resultCount}` : ''}
                                        </span>
                                    ))}
                                </dd>
                            </div>
                        )}
                    </dl>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

/** Event type → its label under SHOPPING_ASSISTANT.LABELS. */
const ACTION_LABEL: Record<string, string> = {
    chat_opened: 'CHAT_OPENED',
    product_link_clicked: 'PRODUCT_LINK',
    cart_clicked: 'CART',
    see_in_room_clicked: 'SEE_IN_ROOM',
    visualize_clicked: 'VISUALIZE',
    open_space_clicked: 'OPEN_SPACE',
};

const averageLatency = (detail: ConversationDetail) => {
    const values = detail.messages.map((m) => m.latencyMs).filter((v): v is number => typeof v === 'number');
    return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
};

const ConversationsBody = () => {
    const { t } = useTranslation();
    const api = useShopperInsightsApi();
    const { range, periodProps } = useInsightsRange();
    const [params, setParams] = useSearchParams();
    const outcomeLabel = useOutcomeLabel();
    const deviceLabel = useDeviceLabel();

    const initial = params.get('outcome');
    const [filter, setFilter] = useState<Filter>(isFilter(initial) ? initial : 'all');
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [result, setResult] = useState<ConversationsPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState<ShopperOverview['conversationOutcomes'] | null>(null);

    const openUuid = params.get('open');
    const [detail, setDetail] = useState<ConversationDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // The shop's team in the chat: who is waiting, live over the socket (shopper-handoff).
    const { summary: team, refresh: refreshTeam } = useHandoffSummary();
    const [tick, setTick] = useState(0);
    const [teamBusy, setTeamBusy] = useState(false);
    const openRef = useRef<string | null>(openUuid);
    openRef.current = openUuid;
    const reloadDetail = useCallback(async (uuid: string) => {
        try {
            const next = await api.getConversation(uuid);
            if (openRef.current === uuid) setDetail(next);
        } catch {
            // The open copy stays; the next event or reopening refreshes it.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => onRealtime(HANDOFF_LIVE_EVENT, (payload: any) => {
        setTick((n) => n + 1);
        if (payload?.threadUuid && payload.threadUuid === openRef.current) void reloadDetail(payload.threadUuid);
    }), [reloadDetail]);
    const showTeamFilters = !!team && (team.enabled || team.waiting > 0 || team.active > 0);

    // Search after typing pauses, not on every key.
    useEffect(() => {
        const timer = setTimeout(() => { setAppliedSearch(search.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => { setPage(1); }, [range.startDate, range.endDate, filter]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.getConversations({
            ...range,
            page,
            limit: PAGE_SIZE,
            search: appliedSearch,
            ...(isTeamFilter(filter) ? { handoff: filter } : { outcome: filter === 'all' ? undefined : filter }),
        })
            .then((r) => { if (!cancelled) setResult(r); })
            .catch((error) => { console.error('Error loading conversations:', error); if (!cancelled) setResult(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate, page, appliedSearch, filter, tick]);

    // The counts on the filters: how the period's conversations ended. Optional — the pills work without them.
    useEffect(() => {
        let cancelled = false;
        api.getOverview(range)
            .then((o) => { if (!cancelled) setCounts(o.conversationOutcomes ?? null); })
            .catch(() => { if (!cancelled) setCounts(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate]);

    // The open conversation lives in the URL, so a link from the dashboard or the questions page lands on it.
    useEffect(() => {
        if (!openUuid) { setDetail(null); return; }
        let cancelled = false;
        setDetailLoading(true);
        api.getConversation(openUuid)
            .then((d) => { if (!cancelled) setDetail(d); })
            .catch((error) => {
                // A conversation beyond the plan's window: say so, and do not leave an empty sheet open.
                const limit = readPlanError(error);
                if (limit) {
                    openUnlockDialog(limit);
                    if (!cancelled) closeDetail();
                    return;
                }
                console.error('Error loading conversation:', error);
                if (!cancelled) setDetail(null);
            })
            .finally(() => { if (!cancelled) setDetailLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openUuid]);

    const open = (uuid: string | null) => {
        const next = new URLSearchParams(params);
        if (uuid) next.set('open', uuid); else next.delete('open');
        setParams(next, { replace: !uuid });
    };
    const closeDetail = () => open(null);

    const changeFilter = (value: Filter) => {
        setFilter(value);
        const next = new URLSearchParams(params);
        if (value === 'all') next.delete('outcome'); else next.set('outcome', value);
        setParams(next, { replace: true });
    };

    const rows = result?.data || [];
    const pagination = result?.pagination;
    const locked = Math.max(0, Number(result?.lockedCount) || 0);
    const lastPage = !pagination || !pagination.hasNext;
    const filtered = filter !== 'all' || !!appliedSearch;

    return (
        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-5">
            <AssistantHeader
                title={t('SHOPPER_UI.CONVERSATIONS.TITLE')}
                subtitle={t('SHOPPER_UI.CONVERSATIONS.SUBTITLE')}
                actions={<PeriodControl {...periodProps} />}
            />
            <HistoryLimitNotice period={range} />

            <div className="flex flex-wrap items-center justify-between gap-3">
                <PillTabs<Filter>
                    value={filter}
                    onChange={changeFilter}
                    ariaLabel={t('SHOPPER_UI.CONVERSATIONS.FILTER_LABEL')}
                    options={FILTERS.filter((key) => showTeamFilters || !isTeamFilter(key)).map((key) => ({
                        value: key,
                        label: key === 'all'
                            ? t('SHOPPER_UI.CONVERSATIONS.FILTER_ALL')
                            : key === 'waiting'
                                ? t('SHOPPER_TEAM.FILTER_WAITING')
                                : key === 'live'
                                    ? t('SHOPPER_TEAM.FILTER_LIVE')
                                    : outcomeLabel(key),
                        count: key === 'all'
                            ? undefined
                            : key === 'waiting'
                                ? team?.waiting
                                : key === 'live'
                                    ? team?.active
                                    : counts ? counts[key] : undefined,
                    }))}
                />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder={t('SHOPPER_UI.CONVERSATIONS.SEARCH')}
                />
            </div>

            <div className="overflow-hidden rounded-xl border bg-card">
                {loading ? (
                    <div className="flex flex-col divide-y">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="flex items-start gap-3 px-4 py-4">
                                <Skeleton className="size-9 rounded-full" />
                                <div className="flex flex-1 flex-col gap-2">
                                    <Skeleton className="h-4 w-1/3" />
                                    <Skeleton className="h-4 w-2/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : rows.length === 0 && !locked ? (
                    <EmptyState
                        icon={<MessagesSquare />}
                        title={filtered ? t('SHOPPER_UI.CONVERSATIONS.EMPTY_FILTERED') : t('SHOPPER_UI.CONVERSATIONS.EMPTY_TITLE')}
                        description={filtered ? undefined : t('SHOPPER_UI.CONVERSATIONS.EMPTY_TEXT')}
                    />
                ) : (
                    <ul className="flex flex-col divide-y" data-testid="conversation-inbox">
                        {rows.map((row) => {
                            const outcome = conversationOutcomeOf(row);
                            return (
                                <li key={row.uuid}>
                                    <button
                                        type="button"
                                        onClick={() => open(row.uuid)}
                                        className={cn(
                                            'flex w-full items-start gap-3 px-4 py-3.5 text-start transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none',
                                            openUuid === row.uuid && 'bg-muted/40',
                                        )}
                                    >
                                        <ShopperAvatar device={row.deviceType} />
                                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                                            <span className="flex items-baseline justify-between gap-3">
                                                <span className="truncate text-sm font-medium">
                                                    {t(`SHOPPER_UI.CONVERSATIONS.FROM.${String(row.deviceType || 'unknown').toUpperCase()}`, {
                                                        defaultValue: t('SHOPPER_UI.CONVERSATIONS.FROM.UNKNOWN'),
                                                    })}
                                                </span>
                                                <RelativeTime value={row.lastMessageAt} className="shrink-0 text-xs text-muted-foreground" />
                                            </span>
                                            <span className="line-clamp-2 text-sm text-muted-foreground">
                                                {row.firstQuestion || (row.roomPhotos ? t('SHOPPER_UI.CONVERSATIONS.PHOTO_SENT') : t('SHOPPER_UI.COMMON.NO_QUESTION'))}
                                            </span>
                                            <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                                <TeamBadge handoff={row.handoff} />
                                                <OutcomeBadge outcome={outcome} />
                                                <span className="text-xs text-muted-foreground">
                                                    {t('SHOPPER_UI.CONVERSATIONS.QUESTIONS_COUNT', { count: row.questions })}
                                                </span>
                                                {row.productSku && (
                                                    <span className="truncate text-xs text-muted-foreground">
                                                        {t('SHOPPER_UI.CONVERSATIONS.ON_PRODUCT', { sku: row.productSku })}
                                                    </span>
                                                )}
                                                <ProductThumbs products={row.products} total={row.productsShown} />
                                            </span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
                {/* The period's conversations the plan does not list, after the ones it does. */}
                {!loading && lastPage && (
                    <UnlockRows count={locked} noun="conversations" reason="maxConversationsVisible" testId="conversations-locked-rows" className={cn(rows.length === 0 && 'border-t-0')} />
                )}
            </div>

            {pagination && pagination.total > PAGE_SIZE && (
                <div className="flex items-center justify-end gap-3 text-sm">
                    <span className="tabular-nums text-muted-foreground">
                        {t('SHOPPER_UI.CONVERSATIONS.PAGE_OF', {
                            from: formatNumber((pagination.page - 1) * pagination.limit + 1),
                            to: formatNumber(Math.min(pagination.page * pagination.limit, pagination.total)),
                            total: formatNumber(pagination.total),
                        })}
                    </span>
                    <Button variant="outline" size="icon-sm" disabled={!pagination.hasPrev} onClick={() => setPage((p) => p - 1)}
                        aria-label={t('SHOPPER_UI.COMMON.PREVIOUS')}>
                        <ChevronLeft className="size-4 rtl:rotate-180" />
                    </Button>
                    <Button variant="outline" size="icon-sm" disabled={!pagination.hasNext} onClick={() => setPage((p) => p + 1)}
                        aria-label={t('SHOPPER_UI.COMMON.NEXT')}>
                        <ChevronRight className="size-4 rtl:rotate-180" />
                    </Button>
                </div>
            )}

            <Sheet open={!!openUuid} onOpenChange={(value) => { if (!value) open(null); }}>
                <SheetContent side="right" className="max-w-[100vw] gap-0 overflow-y-auto p-0 data-[side=right]:w-[600px] data-[side=right]:sm:max-w-[92vw]">
                    <SheetHeader className="border-b p-6 pe-14">
                        <SheetTitle className="text-base font-medium">{t('SHOPPER_UI.CONVERSATIONS.DETAIL_TITLE')}</SheetTitle>
                        <SheetDescription className="text-xs">
                            {detail
                                ? [formatDateTime(detail.startedAt), deviceLabel(detail.deviceType), t('SHOPPER_UI.CONVERSATIONS.QUESTIONS_COUNT', { count: detail.questions })].join(' · ')
                                : ''}
                        </SheetDescription>
                        {detail && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <OutcomeBadge outcome={conversationOutcomeOf(detail)} />
                                {detail.productSku && (
                                    <span className="text-xs text-muted-foreground">{t('SHOPPER_UI.CONVERSATIONS.ON_PRODUCT', { sku: detail.productSku })}</span>
                                )}
                                {detail.pageUrl && (
                                    <a
                                        className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                        href={detail.pageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('SHOPPER_UI.CONVERSATIONS.OPEN_PAGE')}
                                        <ExternalLink className="size-3" />
                                    </a>
                                )}
                            </div>
                        )}
                    </SheetHeader>
                    {detail?.handoff && !detailLoading && (
                        <TeamStatus
                            handoff={detail.handoff}
                            busy={teamBusy}
                            onTake={async () => {
                                setTeamBusy(true);
                                try {
                                    const { handoff } = await api.take(detail.uuid);
                                    setDetail((d) => (d ? { ...d, handoff } : d));
                                    void refreshTeam();
                                } catch (error) {
                                    console.error('Could not take the conversation:', error);
                                } finally {
                                    setTeamBusy(false);
                                }
                            }}
                            onClose={async () => {
                                setTeamBusy(true);
                                try {
                                    const { handoff } = await api.close(detail.uuid);
                                    setDetail((d) => (d ? { ...d, handoff } : d));
                                    void refreshTeam();
                                    setTick((n) => n + 1);
                                } catch (error) {
                                    console.error('Could not hand the conversation back:', error);
                                } finally {
                                    setTeamBusy(false);
                                }
                            }}
                        />
                    )}
                    {detailLoading || !detail ? (
                        <div className="flex flex-col gap-3 p-6">
                            <Skeleton className="h-14 w-3/4" /><Skeleton className="ms-auto h-20 w-3/4" /><Skeleton className="h-14 w-2/3" />
                        </div>
                    ) : (
                        <Transcript detail={detail} />
                    )}
                    {detail && !detailLoading && (team?.enabled || detail.handoff) && (
                        <TeamComposer
                            hint={handoffOpen(detail.handoff)
                                ? (detail.handoff?.shopperEmail ? t('SHOPPER_TEAM.EMAIL_NOTE') : undefined)
                                : t('SHOPPER_TEAM.REPLY_HINT')}
                            onSend={async (text) => {
                                try {
                                    const { handoff, message } = await api.reply(detail.uuid, text);
                                    setDetail((d) => (d
                                        ? { ...d, handoff, messages: [...d.messages, { ...message, author: { name: message.author?.name } }] }
                                        : d));
                                    void refreshTeam();
                                    setTick((n) => n + 1);
                                    return true;
                                } catch (error) {
                                    console.error('Could not send the reply:', error);
                                    return false;
                                }
                            }}
                        />
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export const AssistantConversationsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.SHOPPING_ASSISTANT.TITLE', 'Shopping Assistant') }, { label: t('SIDEBAR.SHOPPING_ASSISTANT.CONVERSATIONS', 'Conversations') }]}>
            <FeatureGate feature="shopperChat">
                <ConversationsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AssistantConversationsPage;
